/**
 * Date helpers of the booking cards. Slot instants are UTC; days are `YYYY-MM-DD` keys in the
 * visitor's time zone (the server groups them that way), so a key is never turned into a local
 * `Date` - it is shifted and formatted as a calendar date in UTC.
 */
const FALLBACK_TIME_ZONE = "UTC";
const MS_PER_DAY = 86_400_000;
const NOON_UTC_HOUR = 12;

export function visitorTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `YYYY-MM-DD` of an instant in `timeZone`. */
export function dateKey(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function keyToUtcNoon(key: string): Date {
  const [year = 1970, month = 1, day = 1] = key.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, NOON_UTC_HOUR));
}

export function addDays(key: string, days: number): string {
  const shifted = new Date(keyToUtcNoon(key).getTime() + days * MS_PER_DAY);
  return `${String(shifted.getUTCFullYear())}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** Earlier of two date keys (ISO dates compare as strings). */
export function minKey(a: string, b: string): string {
  return a <= b ? a : b;
}

export interface DayChipText {
  weekday: string;
  date: string;
}

export function formatDayChip(key: string, locale?: string): DayChipText {
  const noon = keyToUtcNoon(key);
  return {
    weekday: new Intl.DateTimeFormat(locale, { timeZone: "UTC", weekday: "short" }).format(noon),
    date: new Intl.DateTimeFormat(locale, { timeZone: "UTC", month: "short", day: "numeric" }).format(noon),
  };
}

export function formatDayLong(key: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", weekday: "long", month: "long", day: "numeric" }).format(keyToUtcNoon(key));
}

export function formatTime(instant: string, timeZone: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(instant));
}

/** `9:00 AM – 9:45 AM`. */
export function formatTimeRange(start: string, end: string | undefined, timeZone: string, locale?: string): string {
  const from = formatTime(start, timeZone, locale);
  return end ? `${from} – ${formatTime(end, timeZone, locale)}` : from;
}

/** `Tue, Oct 20, 2026` of an instant in `timeZone`. */
export function formatDate(instant: string, timeZone: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone, weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(new Date(instant));
}

/** Short zone name at that instant (`PDT`, `GMT+8`). */
export function zoneName(instant: string, timeZone: string, locale?: string): string {
  const parts = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" }).formatToParts(new Date(instant));
  return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
}
